// =============================================================================
// Origin UI — src/hooks/useAuth.ts
// Custom hook that wraps MSAL to provide convenient access to:
//   - Authentication state (isAuthenticated, isLoading)
//   - User information (name, email)
//   - Role derivation from /api/me endpoint (Phase 1 DB-driven roles)
//   - Token acquisition for API calls
//
// Phase 1 update: Roles are now fetched from /api/me which reads from the
// UserRoleAssignments DB table (via ClaimsTransformation). This replaces
// the previous approach of decoding roles from the Entra ID access token.
//
// Dual-identity fix: Uses MSAL active account (set by LOGIN_SUCCESS handler
// in App.tsx) rather than getAllAccounts()[0], so we never accidentally use
// a cached @oq.com token when the user logged in with @omantrading.com.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '@/config/authConfig';

/** Application roles — Phase 1 role model. */
export interface UserRoles {
  isHRAdmin: boolean;
  isITOps: boolean;
  isSysAdmin: boolean;
  isCreditCardAdmin: boolean;
  isCompliance: boolean;
  isComplianceExec: boolean;
  isOQTMan: boolean;
  isManager: boolean;
  isCardholder: boolean;
  // Derived convenience flags (backward compat)
  isAdmin: boolean;    // HRAdmin OR SysAdmin
  isAuditor: boolean;  // maps to HRAdmin
  // Requester is implicit (any authenticated user)
  // Approver is contextual (resolved per-request, not from token)
}

/** Return type of the useAuth hook. */
export interface AuthInfo {
  isAuthenticated: boolean;
  isLoading: boolean;
  userName: string;
  userEmail: string;
  userId: string;
  roles: UserRoles;
  rolesLoaded: boolean;
  /** Acquire an access token for the Origin API. */
  getApiToken: () => Promise<string>;
  /** Sign the user out. */
  logout: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const DEFAULT_ROLES: UserRoles = {
  isHRAdmin: false,
  isITOps: false,
  isSysAdmin: false,
  isCreditCardAdmin: false,
  isCompliance: false,
  isComplianceExec: false,
  isOQTMan: false,
  isManager: false,
  isCardholder: false,
  isAdmin: false,
  isAuditor: false,
};

/**
 * Hook that provides authentication state, user info, roles, and token acquisition.
 * Roles are fetched from /api/me which reads DB-assigned roles.
 */
export function useAuth(): AuthInfo {
  const { instance, accounts, inProgress } = useMsal();

  // Use the active account (set by LOGIN_SUCCESS handler) instead of
  // accounts[0], which could be a stale @oq.com token from the browser's
  // existing Outlook/Teams session.
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0] ?? null;

  const isAuthenticated = accounts.length > 0;
  const isLoading = inProgress !== 'none';

  const [roles, setRoles] = useState<UserRoles>(DEFAULT_ROLES);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const getApiToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account');

    try {
      const response = await instance.acquireTokenSilent({
        scopes: apiScopes.default,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: apiScopes.default,
          account,
        });
        return response.accessToken;
      }
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId, instance]);

  // Fetch roles from /api/me once authenticated
  useEffect(() => {
    if (!account || isLoading) return;

    let cancelled = false;

    async function fetchRoles() {
      try {
        const token = await instance.acquireTokenSilent({
          scopes: apiScopes.default,
          account: account!,
        });

        const response = await fetch(`${API_BASE_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token.accessToken}` },
        });

        if (!response.ok) {
          console.warn('Failed to fetch /api/me:', response.status);
          return;
        }

        const data = await response.json();
        if (!cancelled && data.roles) {
          setRoles({
            isHRAdmin: data.roles.isHRAdmin ?? false,
            isITOps: data.roles.isITOps ?? false,
            isSysAdmin: data.roles.isSysAdmin ?? false,
            isCreditCardAdmin: data.roles.isCreditCardAdmin ?? false,
            isCompliance: data.roles.isCompliance ?? false,
            isComplianceExec: data.roles.isComplianceExec ?? false,
            isOQTMan: data.roles.isOQTMan ?? false,
            isManager: data.roles.isManager ?? false,
            isCardholder: data.roles.isCardholder ?? false,
            isAdmin: data.roles.isAdmin ?? false,
            isAuditor: data.roles.isAuditor ?? false,
          });
          setRolesLoaded(true);
        }
      } catch (err) {
        console.warn('Error fetching roles from /api/me:', err);
        // Roles stay as defaults — user will still be authenticated
        // but won't see role-gated content
        if (!cancelled) setRolesLoaded(true);
      }
    }

    fetchRoles();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId, instance, isLoading]);

  const userName = account?.name ?? 'Unknown User';
  const userEmail =
    account?.username ??
    (account?.idTokenClaims as Record<string, unknown>)?.email as string ??
    '';
  const userId =
    (account?.idTokenClaims as Record<string, unknown>)?.oid as string ?? '';

  const logout = () => {
    instance.logoutRedirect();
  };

  return {
    isAuthenticated,
    isLoading,
    userName,
    userEmail,
    userId,
    roles,
    rolesLoaded,
    getApiToken,
    logout,
  };
}
